import { FormSection, FormSubmitButton } from 'components/form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import locale from 'date-fns/locale/en-US';
import { Typography, Box, TextField, Tooltip, Chip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Error, HelpOutline } from '@mui/icons-material';
import { useLazyCloneDeviceQuery } from 'store/services';
import { GridCheckCircleIcon } from '@mui/x-data-grid';
import { useFormState } from 'hooks/useFormState';
import { Device } from 'types';
import { useFormContext } from 'react-hook-form';

export function CloneDeviceModalSection({
    newDeviceId,
    oldDeviceId,
}: {
    newDeviceId: string;
    oldDeviceId: string;
}) {
    const { setValue } = useFormContext();

    const [cloneDate, setCloneDate] = React.useState<Date | null>(new Date());

    const [cloneQuery] = useLazyCloneDeviceQuery();

    const [isCloning, setIsCloning] = React.useState(false);
    const [cloneComplete, setCloneComplete] = React.useState(false);

    const { setIsFormSubmitDisabled, currentFormSectionIndex, formSections } =
        useFormState();

    const formSectionTitle = 'Clone';

    // Manage form submit button state
    useEffect(() => {
        if (
            formSections.indexOf(formSectionTitle) === currentFormSectionIndex
        ) {
            if (cloneComplete) {
                setIsFormSubmitDisabled(false);
            }
        } else {
            setIsFormSubmitDisabled(true);
        }
    }, [
        cloneComplete,
        setIsFormSubmitDisabled,
        currentFormSectionIndex,
        formSections,
    ]);

    const handleClone = () => {
        setDeviceSearchError({
            isErrorState: false,
            message: null,
        });
        if (cloneDate) {
            setIsCloning(true);
            cloneQuery({
                cloneFromDeviceId: oldDeviceId,
                cloneToDeviceId: newDeviceId,
                opts: {
                    includeReadings: true,
                    reading_start: cloneDate.getTime(),
                },
            })
                .then((res) => {
                    if (res.data) {
                        const cloned_device: Device | null = res.data;

                        setValue('customer_name', cloned_device?.customer_name);
                        setValue('name', cloned_device?.name);
                        setValue('fuelType', cloned_device?.fuelType);
                        setValue('gaugeType', cloned_device?.gaugeType);
                        setValue('orientation', cloned_device?.orientation);
                        setValue('capacity', cloned_device?.capacity);
                        setValue('address', cloned_device?.address);

                        setCloneComplete(true);
                    } else if (res.error && 'data' in res.error) {
                        const errorMessage = res.error.data;
                        if (
                            typeof errorMessage === 'string' ||
                            errorMessage === null
                        ) {
                            setDeviceSearchError({
                                isErrorState: true,
                                message: errorMessage,
                            });
                        }
                    }
                })
                .finally(() => {
                    setIsCloning(false);
                });
        }
    };

    const [deviceSearchError, setDeviceSearchError] = useState<{
        isErrorState: boolean;
        message: string | null;
    }>({
        isErrorState: false,
        message: null,
    });

    return (
        <FormSection title="Clone">
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                <Typography variant="h6">Clone Device Data</Typography>

                <Tooltip
                    title="Clone the data from the old device id to the new device id."
                    enterTouchDelay={0}
                    placement="bottom"
                    style={{ marginLeft: '0.5rem' }}
                >
                    <HelpOutline color="action" fontSize="small" />
                </Tooltip>
            </Box>

            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {cloneComplete ? (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <GridCheckCircleIcon style={{ color: 'green' }} />
                        <Typography variant="h6">Clone complete!</Typography>
                    </Box>
                ) : (
                    // If clone date is set, show clone button, else show date picker
                    <Box>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{ margin: '1rem' }}
                                >
                                    Old ID: {oldDeviceId}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ margin: '1rem' }}
                                >
                                    New ID: {newDeviceId}
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                            }}
                        >
                            <Tooltip
                                title="All readings from the old device will be cloned to the new device starting at this date."
                                enterTouchDelay={0}
                                placement="bottom"
                                style={{ margin: '0.5rem' }}
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={locale}
                            >
                                <DatePicker
                                    label="Select Readings Start Date"
                                    value={cloneDate}
                                    onChange={(newDate) =>
                                        setCloneDate(newDate)
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                />
                            </LocalizationProvider>
                            <Box style={{ marginLeft: '1rem' }}>
                                <FormSubmitButton
                                    onValidHandler={() => {
                                        return true;
                                    }}
                                    onErrorHandler={() => {
                                        return true;
                                    }}
                                    variant="contained"
                                    onClick={handleClone}
                                    isLoading={isCloning}
                                    buttonLabel="Clone"
                                />
                            </Box>
                        </Box>

                        {deviceSearchError.isErrorState && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                }}
                            >
                                <Chip
                                    icon={<Error />}
                                    label={deviceSearchError.message}
                                    color="error"
                                />
                            </div>
                        )}
                    </Box>
                )}
            </Box>
        </FormSection>
    );
}
